/**
 * Zentraler API-Helper.
 * Spricht apirolf.kispro.de/v1/KISproMainApp/* an, schickt das HttpOnly-Cookie
 * `kispro_access_token` automatisch mit (credentials: 'include').
 *
 * Konvention der Backend-Responses:
 *   { success: true, data: ... } | { success: false, error: { message } }
 * Manche Endpunkte liefern { success, message, ... } direkt – beides wird unterstützt.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const LOGIN_BASE = import.meta.env.VITE_LOGIN_BASE_URL;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export type ApiEnvelope<T> = { success: true; data: T } | { success: false; error: { message: string } };

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** wenn true wird die Body-Struktur nicht JSON-stringifiziert (FormData) */
  raw?: boolean;
  /** Query-Parameter (werden URL-encoded angehängt) */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** wenn true bei 401 NICHT redirecten (z.B. für Login-Checks) */
  skipAuthRedirect?: boolean;
}

function buildUrl(path: string, query?: ApiOptions['query']): string {
  const url = new URL(path.startsWith('http') ? path : API_BASE + (path.startsWith('/') ? path : `/${path}`));
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

function redirectToLogin(): never {
  const redirect = encodeURIComponent(window.location.href);
  window.location.href = `${LOGIN_BASE}/login?reason=session_expired&redirect=${redirect}`;
  // never reached
  throw new ApiError(401, 'Session abgelaufen');
}

/**
 * Macht einen API-Call und gibt das ausgepackte `data` zurück.
 * Wirft `ApiError` bei HTTP- oder Logik-Fehlern.
 */
export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { body, raw, query, skipAuthRedirect, headers, ...rest } = opts;

  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && !raw ? { 'Content-Type': 'application/json' } : {}),
      ...(headers as Record<string, string>),
    },
  };

  if (body !== undefined) {
    init.body = raw ? (body as BodyInit) : JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), init);
  } catch (networkError) {
    throw new ApiError(0, (networkError as Error).message || 'Netzwerkfehler');
  }

  if (response.status === 401 && !skipAuthRedirect) {
    redirectToLogin();
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const msg =
      (parsed as { error?: { message?: string } })?.error?.message ??
      (parsed as { message?: string })?.message ??
      response.statusText;
    throw new ApiError(response.status, msg || `HTTP ${response.status}`, parsed);
  }

  // Envelope: { success, data } | { success: false, error }
  if (parsed && typeof parsed === 'object' && 'success' in parsed) {
    const env = parsed as { success: boolean; data?: T; error?: { message?: string } };
    if (env.success === false) {
      throw new ApiError(response.status, env.error?.message ?? 'API-Fehler', parsed);
    }
    return (env.data !== undefined ? env.data : (env as unknown as T));
  }

  return parsed as T;
}

/** Convenience-Wrapper */
export const apiGet = <T = unknown>(path: string, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
  api<T>(path, { ...opts, method: 'GET' });

export const apiPost = <T = unknown>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method'>) =>
  api<T>(path, { ...opts, method: 'POST', body });

export const apiPut = <T = unknown>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method'>) =>
  api<T>(path, { ...opts, method: 'PUT', body });

export const apiPatch = <T = unknown>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method'>) =>
  api<T>(path, { ...opts, method: 'PATCH', body });

export const apiDelete = <T = unknown>(path: string, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
  api<T>(path, { ...opts, method: 'DELETE' });
